import { Button, CardProps, DialogActions, DialogContent, DialogProps, DialogTitle, Divider } from '@material-ui/core';
import BlockIcon from '@material-ui/icons/Block';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import HighlightOffOutlinedIcon from '@material-ui/icons/HighlightOffOutlined';
import LinkIcon from '@material-ui/icons/Link';
import LinkOffOutlinedIcon from '@material-ui/icons/LinkOffOutlined';
import { calcProductCatalogDetailRoute } from 'app/main/inventoryManagement/routes';
import { TextLinkButton } from 'framework/components/buttons/TextLinkButton';
import { CardX } from 'framework/components/cards/CardX';
import { ChipX } from 'framework/components/chips/ChipX';
import { ColorX } from 'framework/components/color/ColorX';
import { useThemeColor } from 'framework/components/color/useThemeColor';
import { DownloadMultipleIcon } from 'framework/components/icons/DownloadMultipleIcon';
import { FilterOffOutlineIcon } from 'framework/components/icons/FilterOffOutlineIcon';
import { InnerPageXWithStickySidebar } from 'framework/components/layout/InnerPageXWithStickySidebar';
import { QueryResultPaginationX } from 'framework/components/pagination/QueryResultPaginationX';
import { SearchbarX } from 'framework/components/search/SearchbarX';
import { Typography16pxBold } from 'framework/components/typography/Typography16pxBold';
import { TypographyBoldCaption } from 'framework/components/typography/TypographyBoldCaption';
import { AlertDialog } from 'framework/dialogs/AlertDialog';
import { DialogX } from 'framework/dialogs/DialogX';
import { BooleanRadioFilterComponent } from 'framework/filter/BooleanRadioFilterComponent';
import { useApiEffect } from 'framework/hooks/useApiEffect';
import { useFormSubmit } from 'framework/hooks/useFormSubmit';
import { useLazyEffect } from 'framework/hooks/useLazyEffect';
import { useQueryContext } from 'framework/hooks/useQueryContext';
import { useSnackbarNotify } from 'framework/hooks/useSnackbarNotify';
import { onResetToPage1 } from 'framework/types/onResetToPage1';
import { clearParams } from 'framework/utils/clearParams';
import { isFiltered } from 'framework/utils/isFiltered';
import { useAccordionState } from 'framework/utils/useAccordionState';
import {
	exactOnlineCostCentersQuery_all,
	exactOnlineGLAccountsQuery_export,
	exactOnlineProductCatalogItemsCommand_linkCostCenter,
	exactOnlineProductCatalogItemsCommand_unlink,
	exactOnlineProductCatalogItemsCommand_unlinkCostCenter,
	IExactOnlineCostCenter,
	IExactOnlineProductCatalogItemDto,
	IExactOnlineProductCatalogItemsQueryParams,
	IExactOnlineSettingsDto,
} from 'gen/ApiClient';
import { useDownload } from 'gen/useDownload';
import { useLocalization } from 'localization/useLocalization';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDialogsContext } from 'shared/dialogs/useDialogsContext';
import { IReloadTriggerProps } from 'shared/layout/IReloadTriggerProps';
import { ExactOnlineCostCenterCardX, filterCostCenters } from '../exact-online-cost-centers.components';
import { ExactOnlineProductCatalogItemsQueryContext } from './ExactOnlineProductCatalogItemsQueryContext';
import { LinkExactOnlineProductCatalogItemWithExactOnlineGLAccountDialog } from './link/LinkExactOnlineProductCatalogItemWithExactOnlineGLAccountDialog';

type ExactOnlineProductCatalogItemsListTabProps = IReloadTriggerProps & {
	reload: VoidFunction;
	settings: IExactOnlineSettingsDto;
};

export const ExactOnlineProductCatalogItemsListTab = ({ reload, reloadTrigger, settings }: ExactOnlineProductCatalogItemsListTabProps) => {
	const strings = useLocalization();
	const { reload: reloadQuery, queryResult, params, setParams } = useQueryContext(ExactOnlineProductCatalogItemsQueryContext);
	const [exportGlAccountsToXlsx, isExporting] = useDownload(exactOnlineGLAccountsQuery_export);
	const showCostCenters = useMemo(() => settings.costCenterMode === 'FillIn', [settings]);

	useLazyEffect(() => {
		reloadQuery();
	}, [reloadTrigger]);

	if (queryResult === undefined) {
		return <div></div>;
	}

	return (
		<InnerPageXWithStickySidebar
			maxWidth={1280}
			stickySidebar={
				<div
					className='df-col gap-16 mh100'
					style={{ padding: 16, paddingRight: 8, maxWidth: 400 }}>
					<ExactOnlineProductCatalogItemsListFilterCard
						params={params}
						setParams={setParams}
						style={{ minWidth: 220 }}
						showCostCenters={showCostCenters}
					/>
					<div className='fg1'></div>
					<Divider />
					<TextLinkButton
						startIcon={<DownloadMultipleIcon />}
						onClick={() => exportGlAccountsToXlsx()}
						disabled={isExporting}>
						{`Export GL accounts to xlsx`}
					</TextLinkButton>
				</div>
			}>
			<div
				className='df-col gap-16'
				style={{ padding: 16, paddingLeft: 8 }}>
				<SearchbarX
					placeholder={`${strings.searchOnNameOrExactOnlineCode}...`}
					value={params.searchString}
					onSearch={val => setParams(onResetToPage1({ ...params, searchString: val }))}
				/>
				<CardX className='w100'>
					<div
						className='df-row'
						style={{ gap: 8 }}>
						<Typography16pxBold>{strings.results}</Typography16pxBold>
						<div className='fg1'></div>
					</div>
					<Divider style={{ marginTop: 8 }} />
					{queryResult.values.map(t => (
						<ExactOnlineProductCatalogItemListItem
							key={t.id}
							item={t}
							reload={reload}
							showCostCenters={showCostCenters}
						/>
					))}
					<QueryResultPaginationX
						queryResult={queryResult}
						setPageParams={pageParams => setParams({ ...params, pageParams: pageParams })}
						style={{ justifyContent: 'end', paddingTop: 12, marginBottom: -4 }}
					/>
				</CardX>
			</div>
		</InnerPageXWithStickySidebar>
	);
};

type ExactOnlineProductCatalogItemsListFilterCardProps = CardProps & {
	params: IExactOnlineProductCatalogItemsQueryParams;
	setParams: React.Dispatch<React.SetStateAction<IExactOnlineProductCatalogItemsQueryParams>>;
	showCostCenters: boolean;
};

const exclusions: (keyof IExactOnlineProductCatalogItemsQueryParams)[] = [];

const ExactOnlineProductCatalogItemsListFilterCard = ({
	params,
	setParams,
	style,
	showCostCenters,
	...rest
}: ExactOnlineProductCatalogItemsListFilterCardProps) => {
	const strings = useLocalization();
	const onFilter = (pars: IExactOnlineProductCatalogItemsQueryParams) => setParams(onResetToPage1(pars));
	const isFilteredX = useMemo(() => isFiltered(params, exclusions), [params]);
	const onClear = () => setParams(onResetToPage1(clearParams(params, exclusions)));

	return (
		<CardX
			{...rest}
			style={style}
			className='df-col gap-2'>
			<div className='df-row-ac'>
				<span style={{ fontWeight: '500' }}>{strings.filter}</span>
			</div>
			<Divider style={{ marginTop: 8, marginBottom: 8 }} />
			<TextLinkButton
				startIcon={<FilterOffOutlineIcon />}
				color='secondary'
				hoverColor='secondary'
				disabled={isFilteredX === false}
				onClick={onClear}>
				{strings.clearFilter}
			</TextLinkButton>
			<Divider style={{ marginTop: 8, marginBottom: 8 }} />
			<div
				className='df-col gap-4'
				style={{ alignItems: 'start', marginLeft: -5 }}>
				<BooleanRadioFilterComponent
					label={`${strings.deleted}?`}
					trueText={strings.deleted}
					falseText={strings.notDeleted}
					selected={params.isDeleted}
					setSelected={value => onFilter({ ...params, isDeleted: value })}
					icon={<HelpOutlineIcon />}
				/>
				<BooleanRadioFilterComponent
					label={`${strings.linked}?`}
					trueText={strings.linked}
					falseText={strings.notLinked}
					selected={params.isLinked}
					setSelected={value => onFilter({ ...params, isLinked: value })}
					icon={<HelpOutlineIcon />}
				/>
				{showCostCenters === true && (
					<BooleanRadioFilterComponent
						label={`Kostenplaats?`}
						trueText={strings.yes}
						falseText={strings.no}
						selected={params.hasCostCenter}
						setSelected={value => onFilter({ ...params, hasCostCenter: value })}
						icon={<HelpOutlineIcon />}
					/>
				)}
			</div>
		</CardX>
	);
};

const MajorFontSize = 16;
const MinorFontSize = 13;

type ExactOnlineProductCatalogItemListItemProps = {
	item: IExactOnlineProductCatalogItemDto;
	withoutBorder?: boolean;
	reload: VoidFunction;
	showCostCenters: boolean;
};

export const ExactOnlineProductCatalogItemListItem = ({ item, withoutBorder, reload, showCostCenters }: ExactOnlineProductCatalogItemListItemProps) => {
	const strings = useLocalization();
	const { push } = useHistory();
	const color = useMemo<ColorX>(() => (item.isLinked ? 'success' : item.isDeleted ? 'default' : 'error'), [item]);
	const tColor = useThemeColor(color);
	const { open, confirm, cancel } = useDialogsContext(reload);

	const onUnlink = () => {
		open(
			<AlertDialog
				open
				title={strings.deleteLink}
				content={strings.doYouWantToDeleteExistingLinkWithGLAccountQuestion}
				acceptText={strings.yesCommaContinue}
				rejectText={strings.noCommaGoBack}
				reject={cancel}
				acceptF={() => exactOnlineProductCatalogItemsCommand_unlink({ productCatalogItemId: item.id })}
				accept={confirm}
			/>
		);
	};

	const onLink = () => {
		open(
			<LinkExactOnlineProductCatalogItemWithExactOnlineGLAccountDialog
				open
				item={item}
				confirm={confirm}
				cancel={cancel}
			/>
		);
	};

	const onUnlinkCostCenter = () => {
		open(
			<AlertDialog
				open
				title={strings.deleteLink}
				content={`Wil je kostenplaats verwijderen van dit artikel?`}
				acceptText={strings.yesCommaContinue}
				rejectText={strings.noCommaGoBack}
				reject={cancel}
				acceptF={() => exactOnlineProductCatalogItemsCommand_unlinkCostCenter({ productCatalogItemId: item.id })}
				accept={confirm}
			/>
		);
	};

	const onLinkCostCenter = () => {
		open(
			<LinkProductCatalogItemWithExactOnlineCostCenterForm
				open
				item={item}
				confirm={confirm}
				cancel={cancel}
			/>
		);
	};

	return (
		<div
			className='df-row w100'
			style={{
				borderBottom: withoutBorder === true ? 'unset' : '1px solid rgba(224, 224, 224, 1)',
				padding: '12px 0px',
			}}>
			<div style={{ marginRight: 8, color: tColor, fontSize: 22 }}>
				<ExactOnlineProductCatalogItemListItemStatusIcon
					item={item}
					style={{ fontSize: 22, color: tColor }}
				/>
			</div>
			<div className='df-col gap-4 fg1'>
				<div className='df-row-ac'>
					<TextLinkButton
						onClick={() => push(calcProductCatalogDetailRoute(item.id))}
						style={{ fontSize: MajorFontSize }}>
						{item.identifier}
					</TextLinkButton>
				</div>
				{showCostCenters === true && <TypographyBoldCaption>{`GL account:`}</TypographyBoldCaption>}
				<ChipX
					label={item.isLinked ? strings.linked : item.isDeleted ? strings.deleted : strings.notLinked}
					color={color}
					style={{ marginRight: 'auto' }}
				/>
				{item.isLinked && (
					<>
						<div
							className='df-row-ac'
							style={{ fontSize: MinorFontSize, gap: 4 }}>
							<span>{`${strings.exactOnlineGLAccountCode}:`}</span>
							<TextLinkButton
								style={{ fontSize: MinorFontSize }}
								onClick={() => {}}>
								{item.exactOnlineGLAccountCode}
							</TextLinkButton>
						</div>
						<TextLinkButton
							hoverColor='secondary'
							startIcon={<LinkOffOutlinedIcon />}
							onClick={onUnlink}>
							{strings.deleteLinkImperative}
						</TextLinkButton>
					</>
				)}
				{item.isLinked === false && (
					<TextLinkButton
						color='primary'
						startIcon={<LinkIcon />}
						onClick={onLink}>
						{strings.link}
					</TextLinkButton>
				)}

				{showCostCenters && (
					<>
						<Divider variant='inset' />
						<TypographyBoldCaption>{`Kostenplaats`}</TypographyBoldCaption>
						<ChipX
							label={item.hasCostCenter ? strings.linked : item.isDeleted ? strings.deleted : strings.notLinked}
							color={item.hasCostCenter ? 'success' : 'warning'}
							style={{ marginRight: 'auto' }}
						/>
						{item.hasCostCenter && (
							<>
								<div
									className='df-row-ac'
									style={{ fontSize: MinorFontSize, gap: 4 }}>
									<span>{`Kostenplaats:`}</span>
									<TextLinkButton
										style={{ fontSize: MinorFontSize }}
										onClick={() => {}}>
										{`${item.costCenter?.code} (${item.costCenter?.description})`}
									</TextLinkButton>
								</div>
								<TextLinkButton
									hoverColor='secondary'
									startIcon={<LinkOffOutlinedIcon />}
									onClick={onUnlinkCostCenter}>
									{`Verwijder kostenplaats`}
								</TextLinkButton>
							</>
						)}
						{item.hasCostCenter === false && (
							<TextLinkButton
								color='primary'
								startIcon={<LinkIcon />}
								onClick={onLinkCostCenter}>
								{`Link kostenplaats`}
							</TextLinkButton>
						)}
					</>
				)}
			</div>
		</div>
	);
};

type ExactOnlineProductCatalogItemListItemStatusIconProps = {
	item: IExactOnlineProductCatalogItemDto;
	style?: React.CSSProperties;
};

export const ExactOnlineProductCatalogItemListItemStatusIcon = ({ item, style }: ExactOnlineProductCatalogItemListItemStatusIconProps) => {
	if (item.isLinked) {
		return <CheckCircleOutlineIcon style={style} />;
	} else if (item.isDeleted) {
		return <HighlightOffOutlinedIcon style={style} />;
	} else {
		return <BlockIcon style={style} />;
	}
};

type LinkProductCatalogItemWithExactOnlineCostCenterFormProps = DialogProps & {
	item: IExactOnlineProductCatalogItemDto;
	cancel: VoidFunction;
	confirm: () => void;
};

const LinkProductCatalogItemWithExactOnlineCostCenterForm = ({ item, cancel, confirm, ...rest }: LinkProductCatalogItemWithExactOnlineCostCenterFormProps) => {
	const strings = useLocalization();
	const [costCenters] = useApiEffect(exactOnlineCostCentersQuery_all);
	const [searchString, setSearchString] = useState<string>('');
	const fCostCenters = useMemo(() => filterCostCenters(costCenters ?? [], searchString), [costCenters, searchString]);
	const [selectedCostCenter, setSelectedCostCenter] = useAccordionState<IExactOnlineCostCenter | undefined>();
	const [submit] = useFormSubmit(exactOnlineProductCatalogItemsCommand_linkCostCenter);
	const notify = useSnackbarNotify();

	useEffect(() => {
		setSelectedCostCenter(undefined);
		// eslint-disable-next-line
	}, [searchString]);

	const onSubmit = async () => {
		if (selectedCostCenter === undefined) {
			return;
		}

		const r = await submit({ productCatalogItemId: item.id, costCenterId: selectedCostCenter.id });
		if (r.isFailure) {
			notify(r.error);
		} else {
			confirm();
		}
	};

	return (
		<DialogX
			{...rest}
			open={rest.open}
			fullHeight>
			<DialogTitle>
				<div className='df-col gap-8'>
					<span>{`Link locatie met kostenplaats`}</span>
					<SearchbarX
						placeholder={`${strings.searchOnNameSlashCode}...`}
						onSearch={setSearchString}
						value={searchString}
					/>
				</div>
			</DialogTitle>
			<DialogContent
				className='df-col gap-8'
				dividers>
				{fCostCenters.map(t => (
					<ExactOnlineCostCenterCardX
						key={t.id}
						item={t}
						onSelect={() => setSelectedCostCenter(t)}
						isSelected={t.id === selectedCostCenter?.id}
					/>
				))}
			</DialogContent>
			<DialogActions>
				<Button
					onClick={cancel}
					color='primary'>
					{strings.cancel}
				</Button>
				<Button
					onClick={onSubmit}
					color='primary'
					disabled={selectedCostCenter === undefined}>
					{strings.proceed}
				</Button>
			</DialogActions>
		</DialogX>
	);
};
